import {
  AlertTriangle,
  ArrowRight,
  Backpack,
  CalendarCheck2,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleDashed,
  Clock3,
  Home as HomeIcon,
  MapPin,
  MoreHorizontal,
  ShieldCheck,
  Sparkles,
  Utensils,
  X,
} from "lucide-react";
import type { ProposalListItem } from "@fam/shared";
import { useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { Avatar } from "./components/Avatar";
import { CaptureDialog } from "./components/CaptureDialog";
import { LoginPage } from "./components/LoginPage";
import { family, tasks, timeline } from "./data/starter";
import { apiUrl, decideProposal, editAndApproveProposal, getProposals } from "./api";

function formatToday() {
  return new Intl.DateTimeFormat("en-US", { weekday: "long", month: "long", day: "numeric" }).format(new Date());
}

function PreviewNotice() {
  return <div className="preview-notice"><CircleDashed size={16} /> Starter preview. Connections are not configured yet.</div>;
}

function TodayPage() {
  return (
    <div className="page page-today">
      <header className="page-header">
        <div><span className="eyebrow">{formatToday()}</span><h1>Good afternoon, Seth.</h1><p>Three decisions need you. Tomorrow starts early.</p></div>
        <div className="family-stack" aria-label="Household members">{family.slice(0, 5).map((person) => <Avatar key={person.id} initials={person.initials} color={person.color} size="small" />)}</div>
      </header>
      <PreviewNotice />

      <section className="today-grid" aria-label="Today at a glance">
        <article className="panel timeline-panel">
          <div className="panel-heading"><div><span className="eyebrow">Family timeline</span><h2>Today</h2></div><button className="text-button" type="button">Calendar <ChevronRight size={16} /></button></div>
          <div className="timeline">
            {timeline.map((item) => (
              <div className="timeline-item" key={`${item.time}-${item.title}`}>
                <time>{item.time}</time><span className={`timeline-dot tone-${item.tone}`} />
                <div><strong>{item.title}</strong><span>{item.person}</span></div>
                <span className={`status-label ${item.tone === "alert" ? "danger" : item.tone === "warn" ? "warning" : ""}`}>{item.owner}</span>
              </div>
            ))}
          </div>
        </article>

        <article className="panel decision-panel">
          <div className="panel-heading"><div><span className="eyebrow">Awaiting decision</span><h2>Needs you</h2></div><span className="number-badge">3</span></div>
          <button className="decision-row" type="button"><span className="icon-tile coral"><CalendarCheck2 size={18} /></span><span><strong>Confirm piano visit</strong><small>Date and entry plan are uncertain</small></span><ChevronRight size={18} /></button>
          <button className="decision-row" type="button"><span className="icon-tile gold"><AlertTriangle size={18} /></span><span><strong>Transportation gap</strong><small>School drop-off has no driver</small></span><ChevronRight size={18} /></button>
          <button className="decision-row" type="button"><span className="icon-tile sky"><Utensils size={18} /></span><span><strong>Choose dinner</strong><small>Everyone is home by 5:45</small></span><ChevronRight size={18} /></button>
        </article>

        <article className="panel tasks-panel">
          <div className="panel-heading"><div><span className="eyebrow">Critical tasks</span><h2>Keep moving</h2></div><button className="text-button" type="button">All tasks <ChevronRight size={16} /></button></div>
          <div className="task-list">{tasks.map((task) => <label className="task-row" key={task.title}><input type="checkbox" /><span className={`checkmark priority-${task.priority}`} /><span><strong>{task.title}</strong><small>{task.owner} · {task.due}</small></span><MoreHorizontal size={18} /></label>)}</div>
        </article>

        <article className="panel prep-panel">
          <div className="panel-heading"><div><span className="eyebrow">Tomorrow morning</span><h2>Prepare tonight</h2></div><Backpack size={21} /></div>
          <ul className="prep-list"><li><span className="icon-tile sky"><Backpack size={17} /></span><span><strong>Watson</strong><small>Trumpet, band folder, charger</small></span></li><li><span className="icon-tile gold"><Backpack size={17} /></span><span><strong>Bennet</strong><small>Dance bag, water bottle</small></span></li><li><span className="icon-tile mint"><HomeIcon size={17} /></span><span><strong>House</strong><small>Window inspection before bedtime</small></span></li></ul>
        </article>

        <article className="panel coverage-panel">
          <div className="coverage-flag"><AlertTriangle size={18} /><span><strong>Coverage gap</strong><small>7:45 AM school drop-off</small></span></div>
          <button className="secondary-button" type="button">Assign driver <ArrowRight size={16} /></button>
        </article>

        <article className="panel home-panel">
          <div className="panel-heading"><div><span className="eyebrow">At home</span><h2>House status</h2></div><ShieldCheck size={21} /></div>
          <div className="home-status"><div><span className="status-dot good" /><span><strong>Doors</strong><small>All secure</small></span></div><div><span className="status-dot warn" /><span><strong>Bedroom window</strong><small>Needs inspection</small></span></div><div><span className="status-dot muted" /><span><strong>HUP device</strong><small>Offline</small></span></div></div>
        </article>
      </section>
    </div>
  );
}

function humanize(value: string) {
  return value.replaceAll("_", " ").replace(/^./, (letter) => letter.toUpperCase());
}

function formatReceived(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Unknown";
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }).format(date);
}

function InboxPage() {
  const navigate = useNavigate();
  const [proposals, setProposals] = useState<ProposalListItem[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "failed">("loading");
  const [reloadKey, setReloadKey] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [selectedSourceId, setSelectedSourceId] = useState<string | null>(null);
  const pending = proposals.filter((proposal) => proposal.state === "pending");
  const selectedProposal = proposals.find((proposal) => proposal.inbox_item_id === selectedSourceId) ?? proposals[0];
  const reviewedCount = proposals.length - pending.length;
  const sourceCount = new Set(proposals.map((proposal) => proposal.inbox_item_id)).size;

  useEffect(() => {
    const controller = new AbortController();
    setLoadState("loading");
    void getProposals(controller.signal)
      .then((records) => {
        setProposals(records);
        setSelectedSourceId((current) => current && records.some((record) => record.inbox_item_id === current)
          ? current
          : records[0]?.inbox_item_id ?? null);
        setLoadState("ready");
      })
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setLoadState("failed");
      });
    return () => controller.abort();
  }, [reloadKey]);

  async function review(id: string, decision: "approve" | "reject") {
    setBusyId(id);
    setActionError(null);
    try {
      const result = await decideProposal(id, decision);
      setProposals((current) => current.map((proposal) => proposal.id === id ? { ...proposal, state: result.state } : proposal));
    } catch {
      setActionError("That review could not be saved. Try again.");
    } finally {
      setBusyId(null);
    }
  }

  async function saveEdit(id: string) {
    const title = draftTitle.trim();
    if (!title) return;
    setBusyId(id);
    setActionError(null);
    try {
      const result = await editAndApproveProposal(id, title);
      setProposals((current) => current.map((proposal) => proposal.id === id ? { ...proposal, title: result.title, state: result.state } : proposal));
      setEditingId(null);
      setDraftTitle("");
    } catch {
      setActionError("The edited proposal could not be saved. Try again.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="page">
      <header className="page-header"><div><span className="eyebrow">Life Inbox</span><h1>Review what Fam noticed.</h1><p>{pending.length} proposals are waiting for an adult.</p></div><span className="privacy-chip"><ShieldCheck size={16} /> Adults only</span></header>
      {actionError ? <div className="review-error" role="alert"><AlertTriangle size={16} /> {actionError}</div> : null}
      <div className="review-layout">
        <section className="review-list" aria-label="Pending proposals">
          {loadState === "loading" ? <div className="review-state" aria-live="polite"><CircleDashed size={20} /> Loading review queue</div> : null}
          {loadState === "failed" ? <div className="review-state"><AlertTriangle size={20} /><h2>The review queue could not be loaded.</h2><button className="secondary-button" type="button" onClick={() => setReloadKey((value) => value + 1)}>Try again</button></div> : null}
          {loadState === "ready" && proposals.length === 0 ? <div className="review-state"><CheckCircle2 size={20} /><h2>Nothing is waiting for review.</h2><button className="primary-button" type="button" onClick={() => navigate("/inbox?capture=1")}>Capture something</button></div> : null}
          {loadState === "ready" ? proposals.map((proposal) => (
            <article className={`proposal-card state-${proposal.state}`} key={proposal.id}>
              <div className="proposal-top"><span className="proposal-type">{humanize(proposal.proposal_type)}</span><span className="confidence">{Math.round(proposal.confidence * 100)}% confidence</span></div>
              {editingId === proposal.id
                ? <label className="proposal-title-editor"><span className="sr-only">Proposal title</span><input autoFocus value={draftTitle} onChange={(event) => setDraftTitle(event.target.value)} /></label>
                : <h2>{proposal.title}</h2>}
              <div className="proposal-meta"><span><Avatar initials={proposal.suggested_owner_name?.slice(0, 1) ?? "?"} color={proposal.suggested_owner_name === "Seth" ? "forest" : "coral"} size="small" /> {proposal.suggested_owner_name ?? "Owner not suggested"}</span><span className="sensitivity-chip">{humanize(proposal.sensitivity)}</span></div>
              <blockquote>“{proposal.evidence_excerpt}”</blockquote>
              {proposal.reason ? <p className="proposal-reason">{proposal.reason}</p> : null}
              <button className="source-line" type="button" onClick={() => setSelectedSourceId(proposal.inbox_item_id)}><Sparkles size={15} /><span>Proposed from <strong>{proposal.source_title}</strong></span></button>
              {proposal.state === "pending" && editingId !== proposal.id ? <div className="proposal-actions"><button className="reject-button" type="button" disabled={busyId === proposal.id} onClick={() => void review(proposal.id, "reject")}><X size={17} /> Reject</button><button className="secondary-button" type="button" disabled={busyId === proposal.id} onClick={() => { setEditingId(proposal.id); setDraftTitle(proposal.title); }}>Edit</button><button className="approve-button" type="button" disabled={busyId === proposal.id} onClick={() => void review(proposal.id, "approve")}><Check size={17} /> {busyId === proposal.id ? "Saving" : "Approve"}</button></div> : null}
              {proposal.state === "pending" && editingId === proposal.id ? <div className="proposal-actions"><button className="secondary-button" type="button" disabled={busyId === proposal.id} onClick={() => { setEditingId(null); setDraftTitle(""); }}>Cancel</button><button className="approve-button" type="button" disabled={busyId === proposal.id || !draftTitle.trim()} onClick={() => void saveEdit(proposal.id)}><Check size={17} /> {busyId === proposal.id ? "Saving" : "Save & approve"}</button></div> : null}
              {proposal.state !== "pending" ? <div className={`decision-result ${proposal.state}`}>{proposal.state === "rejected" ? <X size={17} /> : <CheckCircle2 size={17} />} {proposal.state === "edited_approved" ? "Edited and approved" : proposal.state === "approved" ? "Approved for application" : humanize(proposal.state)}</div> : null}
            </article>
          )) : null}
        </section>
        <aside className="inbox-summary"><span className="eyebrow">{selectedProposal ? "Source" : "Review queue"}</span><h2>{selectedProposal?.source_title ?? "Inbox is clear"}</h2><dl>{selectedProposal ? <><div><dt>Received</dt><dd>{formatReceived(selectedProposal.source_created_at)}</dd></div><div><dt>Visibility</dt><dd>{humanize(selectedProposal.source_visibility)}</dd></div><div><dt>Retention</dt><dd>{humanize(selectedProposal.source_retention)}</dd></div><div><dt>Analysis</dt><dd>{humanize(selectedProposal.source_status)}</dd></div></> : <><div><dt>Waiting</dt><dd>{pending.length}</dd></div><div><dt>Reviewed</dt><dd>{reviewedCount}</dd></div><div><dt>Sources</dt><dd>{sourceCount}</dd></div></>}</dl></aside>
      </div>
    </div>
  );
}

function PeoplePage() {
  return <div className="page"><header className="page-header"><div><span className="eyebrow">Household</span><h1>People</h1><p>Schedules, responsibilities, needs, and the work around each person.</p></div></header><PreviewNotice /><section className="people-grid">{family.map((person) => <article className="person-card" key={person.id}><Avatar initials={person.initials} color={person.color} size="large" /><div><h2>{person.name}</h2><span>{person.role}</span></div><div className="person-stats"><span><strong>{person.id === "seth" ? 6 : person.id === "lara" ? 4 : 2}</strong> open</span><span><strong>{person.id === "watson" || person.id === "bennet" ? 2 : 1}</strong> today</span></div><button className="icon-button" type="button" aria-label={`Open ${person.name}`}><ChevronRight size={19} /></button></article>)}</section></div>;
}

function TasksPage() {
  return <div className="page"><header className="page-header"><div><span className="eyebrow">Household work</span><h1>Tasks</h1><p>Four open items, including one without an owner.</p></div><button className="primary-button" type="button">Add task</button></header><PreviewNotice /><section className="full-panel"><div className="task-list expanded">{tasks.map((task) => <label className="task-row" key={task.title}><input type="checkbox" /><span className={`checkmark priority-${task.priority}`} /><span><strong>{task.title}</strong><small>{task.owner}</small></span><span className="due-chip"><Clock3 size={14} /> {task.due}</span><MoreHorizontal size={18} /></label>)}</div></section></div>;
}

function CalendarPage() {
  return <div className="page"><header className="page-header"><div><span className="eyebrow">Schedule</span><h1>Calendar</h1><p>One transportation gap and one event needing confirmation.</p></div></header><PreviewNotice /><section className="full-panel calendar-list">{timeline.map((item) => <div className="calendar-row" key={item.title}><time>{item.time}</time><span className={`timeline-dot tone-${item.tone}`} /><div><strong>{item.title}</strong><small><MapPin size={13} /> {item.person}</small></div><span className="status-label">{item.owner}</span></div>)}</section></div>;
}

type ModuleName = "rhythms" | "home" | "money" | "meals" | "projects" | "us" | "assistant" | "settings";

const moduleCopy: Record<ModuleName, { eyebrow: string; title: string; status: string }> = {
  rhythms: { eyebrow: "Family patterns", title: "Rhythms", status: "Weekly planning is ready to shape together." },
  home: { eyebrow: "Places and assets", title: "Home", status: "One security issue and one offline device need attention." },
  money: { eyebrow: "Read-only", title: "Money", status: "No financial sources are connected." },
  meals: { eyebrow: "This week", title: "Meals", status: "Tonight's meal is undecided." },
  projects: { eyebrow: "Household work", title: "Projects", status: "No active projects have been confirmed." },
  us: { eyebrow: "Protected", title: "Us", status: "Shared records will appear only through an intentional action." },
  assistant: { eyebrow: "Permission-aware", title: "Chief of Staff", status: "Ask from confirmed household data once sources are connected." },
  settings: { eyebrow: "Household", title: "Settings", status: "Connections, people, access, retention, and audit." },
};

function ModulePage({ module }: { module: ModuleName }) {
  const copy = moduleCopy[module];
  return <div className="page"><header className="page-header"><div><span className="eyebrow">{copy.eyebrow}</span><h1>{copy.title}</h1><p>{copy.status}</p></div>{module === "us" ? <span className="privacy-chip"><ShieldCheck size={16} /> Protected</span> : null}</header><section className="empty-state"><span className="icon-tile forest"><Sparkles size={20} /></span><h2>{copy.status}</h2><button className="secondary-button" type="button">Review setup <ArrowRight size={16} /></button></section></div>;
}

export function App() {
  const [captureOpen, setCaptureOpen] = useState(false);
  const [sessionState, setSessionState] = useState<"loading" | "authenticated" | "unauthenticated" | "offline">("loading");
  const location = useLocation();
  const navigate = useNavigate();
  const captureFromRoute = useMemo(() => new URLSearchParams(location.search).get("capture") === "1", [location.search]);

  useEffect(() => {
    const controller = new AbortController();
    void fetch(apiUrl("/api/v1/me"), { credentials: "include", signal: controller.signal })
      .then((response) => {
        setSessionState(response.ok ? "authenticated" : response.status === 401 || response.status === 403 ? "unauthenticated" : "offline");
      })
      .catch(() => setSessionState(import.meta.env.DEV ? "authenticated" : "offline"));
    return () => controller.abort();
  }, []);

  function closeCapture() {
    setCaptureOpen(false);
    if (captureFromRoute) navigate(location.pathname, { replace: true });
  }

  if (sessionState === "loading") {
    return <main className="session-loading"><img src="/fam-mark.svg" alt="" /><span>Opening Fam</span></main>;
  }
  if (sessionState === "unauthenticated") return <LoginPage />;
  if (sessionState === "offline") {
    return <main className="session-loading"><img src="/fam-mark.svg" alt="" /><h1>Fam is unavailable.</h1><p>The private API could not be reached.</p></main>;
  }

  return (
    <AppShell onCapture={() => setCaptureOpen(true)}>
      <Routes>
        <Route path="/" element={<TodayPage />} />
        <Route path="/inbox" element={<InboxPage />} />
        <Route path="/people" element={<PeoplePage />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        {(Object.keys(moduleCopy) as ModuleName[]).map((module) => <Route key={module} path={`/${module}`} element={<ModulePage module={module} />} />)}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <CaptureDialog open={captureOpen || captureFromRoute} onClose={closeCapture} />
    </AppShell>
  );
}
